import i18next from "i18next";
import React, { useEffect, useState, useRef } from "react";
import { FIELD_REQUIRED, LOADING } from "../../../../i18n/keysTranslations";
import { Controller, useController } from "react-hook-form";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslationApp } from "../../../../lib/i18next";
import { getReactHookFormNestedError } from "../../../../utils/errors";
import { useDispatchApp } from "../../../../lib/redux";
import { startSearchApps } from "../../../../actions/adsCampaigns";
import { DELAY_DEBOUNCE } from "../../../../utils/constants";
import { useDebounced } from "../../../../hooks/useDebounce";
import _ from "lodash";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { GOOGLE_APP_STORE } from "../../../../utils/constants";
import Avatar from "@mui/material/Avatar";

const SelectGoogleApp = ({
  control,
  errors,
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled,
  store,
  limit = 20,
  fullWidth,
  variant = "filled",
  placeholder,
  helperText,
  onChangeAction = () => {},
  disableClearable = false,
  sxTextField = {},
}) => {
  const {
    field: { value: appID, onChange: onChangeAppID },
  } = useController({ control, name });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingInitialValue, setLoadingInitialValue] = useState(false);
  const [options, setOptions] = useState([]);
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const searchedAppIDRef = useRef(null);

  const onGetApps = async () => {
    if (!store) return;
    setLoading(true);
    const response = await dispatch(
      startSearchApps({
        store: store,
        search: "",
        limit,
      })
    );
    if (response.ok) {
      const data = response.data;
      if (data?.length > 0) {
        setOptions(data);
      }
      if (appID && !data.find((opt) => opt.appID === appID)) {
        const searchResponse = await dispatch(
          startSearchApps({
            store: store,
            search: appID,
            limit: 10,
          })
        );
        if (searchResponse.ok && searchResponse.data?.length > 0) {
          setOptions((prev) =>
            _.uniqBy([...prev, ...searchResponse.data], "appID")
          );
        }
      }
    }
    setLoading(false);
  };

  const onSearchAppByID = async (appIDToSearch, isInitialLoad = false) => {
    if (!store || !appIDToSearch) return;

    // Avoid duplicate searches
    if (searchedAppIDRef.current === appIDToSearch) return;
    searchedAppIDRef.current = appIDToSearch;

    // If it's initial load, use loadingInitialValue, otherwise use normal loading
    if (isInitialLoad) {
      setLoadingInitialValue(true);
    } else {
      setLoading(true);
    }

    const response = await dispatch(
      startSearchApps({
        store: store,
        search: appIDToSearch,
        limit: 1,
        isSearchByID: true,
      })
    );
    if (response.ok && response.data?.length > 0) {
      const foundApp = response.data.find((opt) => opt.appID === appIDToSearch);
      if (foundApp) {
        setOptions((prev) => {
          const updated = _.uniqBy([...prev, foundApp], "appID");
          return updated;
        });
        // Ensure the value is selected in the form
        onChangeAppID(appIDToSearch);
        onChangeAction(foundApp);
      }
    }

    if (isInitialLoad) {
      setLoadingInitialValue(false);
    } else {
      setLoading(false);
    }
  };

  const onSearchApps = useDebounced(
    async (search) => {
      if (!store || !search) return;
      setLoading(true);
      const response = await dispatch(
        startSearchApps({
          store: store,
          search,
          limit,
        })
      );
      if (response.ok) {
        const data = response.data;
        if (data?.length > 0) {
          setOptions((prev) => _.uniqBy([...prev, ...data], "appID"));
        }
      }
      setLoading(false);
    },
    [DELAY_DEBOUNCE]
  );

  useEffect(() => {
    // Reset the reference when store changes
    searchedAppIDRef.current = null;

    // If there's a saved appID, don't reset the value
    if (!store) {
      setOptions([]);
      onChangeAppID("");
      onChangeAction(null);
      setLoadingInitialValue(false);
      return;
    }

    // If there's an initial saved appID, search for that specific app
    if (appID && store) {
      setOptions([]);
      onSearchAppByID(appID, true); // true indicates it's initial load
    } else {
      // If there's no appID, reset and load initial apps
      setOptions([]);
      onChangeAppID("");
      onChangeAction(null);
      setLoadingInitialValue(false);
      onGetApps();
    }
    // eslint-disable-next-line
  }, [store]);

  // Separate useEffect for when there's an initial saved appID (when the form loads)
  useEffect(() => {
    if (
      appID &&
      store &&
      !options.find((opt) => opt.appID === appID) &&
      searchedAppIDRef.current !== appID
    ) {
      onSearchAppByID(appID, true); // true indicates it's initial load
    }
    // eslint-disable-next-line
  }, [appID]);

  return (
    <>
      <Controller
        defaultValue={""}
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => (
          <Autocomplete
            {...field}
            hasLabel={Boolean(label)}
            fullWidth={fullWidth}
            disabled={disabled || !store || loadingInitialValue}
            disableClearable={disableClearable}
            loadingText={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <CircularProgress color="inherit" size={20} />
                {t(LOADING)}
              </Box>
            }
            loading={loading || loadingInitialValue}
            open={open}
            onOpen={() => {
              if (!loadingInitialValue) {
                setOpen(true);
              }
            }}
            onClose={() => {
              setOpen(false);
            }}
            onInputChange={(event, newInputValue) => {
              if (!newInputValue || !event || loadingInitialValue) return;
              onSearchApps(newInputValue);
            }}
            value={
              field.value
                ? options.find((opt) => opt.appID === field.value) || null
                : null
            }
            onChange={(_, value) => {
              if (value?.appID) {
                field.onChange(value.appID);
                onChangeAction(value);
              } else {
                field.onChange("");
                onChangeAction(null);
              }
            }}
            isOptionEqualToValue={(option, value) =>
              option?.appID === value?.appID
            }
            getOptionLabel={(option) => {
              if (!option) return "";
              if (typeof option === "string") {
                const optionValue = options.find((opt) => opt.appID === option);
                if (optionValue) {
                  return `${optionValue?.appName} - ${optionValue?.appID}`;
                }
                return option;
              }
              return `${option?.appName} - ${option?.appID}`;
            }}
            options={options}
            renderOption={(props, option) => {
              return (
                <Box
                  {...props}
                  component="li"
                  key={option.appID}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 1.5,
                    cursor: "pointer",
                  }}
                >
                  <Thumbnail
                    src={option.thumbnail}
                    alt={option.appName}
                    store={store}
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography variant="body2" noWrap>
                      {option.appName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.appID}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => {
              const selectedOption = options.find(
                (opt) => opt.appID === field.value
              );
              return (
                <TextField
                  id={name}
                  {...params}
                  label={label}
                  variant={variant}
                  hiddenLabel={!label}
                  placeholder={field?.value?.length > 0 ? "" : placeholder}
                  error={!!error}
                  sx={{
                    backgroundColor: "fields.background",
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    ...sxTextField,
                    ".MuiFormHelperText-root": {
                      ml: "0 !important",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: selectedOption ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mx: 0.4,
                        }}
                      >
                        <Thumbnail
                          src={selectedOption.thumbnail}
                          alt={selectedOption.appName}
                          store={store}
                          sx={{
                            width: 26,
                            height: 26,
                          }}
                        />
                      </Box>
                    ) : loadingInitialValue ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mx: 0.4,
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    ) : null,
                    endAdornment: (
                      <React.Fragment>
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              );
            }}
          />
        )}
      />
      {(error?.message || helperText) && (
        <Typography
          variant="caption"
          color={error?.message ? "error" : "fields.placeholder"}
        >
          {error?.message || helperText}
        </Typography>
      )}
    </>
  );
};

const Thumbnail = ({ src, alt, store, sx = {} }) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [referrerPolicy, setReferrerPolicy] = useState("no-referrer");
  const [hasTriedOrigin, setHasTriedOrigin] = useState(false);

  const isGoogle = store === GOOGLE_APP_STORE;

  const onError = () => {
    if (isGoogle && referrerPolicy === "no-referrer" && !hasTriedOrigin) {
      setReferrerPolicy("origin");
      setHasTriedOrigin(true);
      const separator = src.includes("?") ? "&" : "?";
      setImgSrc(`${src}${separator}t=${Date.now()}`);
    }
  };

  useEffect(() => {
    setImgSrc(src);
    setReferrerPolicy("no-referrer");
    setHasTriedOrigin(false);
  }, [src]);

  if (!src) return null;

  if (!isGoogle) {
    return (
      <Avatar
        src={src}
        alt={alt}
        sx={{
          borderRadius: "50%",
          "& .MuiAvatar-img": {
            objectFit: "contain",
          },
          ...sx,
        }}
      />
    );
  }

  return (
    <Box
      component="img"
      src={imgSrc}
      alt={alt}
      crossOrigin="anonymous"
      referrerPolicy={referrerPolicy}
      onError={onError}
      sx={{
        borderRadius: "50%",
        objectFit: "contain",
        backgroundColor: "rgba(0, 0, 0, 0.08)",
        ...sx,
      }}
    />
  );
};

export default SelectGoogleApp;
